import { React, useEffect, useState, useCallback, useRef } from 'react';
import { filterEditorState } from 'draftjs-filters';
import { Grid, withTheme } from '@material-ui/core';
import {
    ContentState,
    convertFromHTML,
    convertFromRaw,
    convertToRaw,
    EditorState,
    getDefaultKeyBinding,
    RichUtils
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import Editor from 'draft-js-plugins-editor';
import {
    LinkPlugin,
    createLinkDecorator,
    promptForLink,
    removeLink,
    LinkPopover
} from './components/LinkPlugin';
import BlockStyleControls from './components/BlockStyleControls';
import InlineStyleControls from './components/InlineStyleControls';
import StyleButton from './components/StyleButton';

/**
 * Parse Draft.js context string to Context (or if raw html, convert it)
 * @param value
 * @returns {EditorState}
 */
export function convertJsonOrStringToContext(value) {
    const decorator = createLinkDecorator();
    let parsedValue = '';
    try {
        parsedValue = JSON.parse(value);
        if (typeof parsedValue !== 'object') {
            throw new Error('Not a valid JSON object');
        }
    } catch (error) {
        // this solves compatibility issues with plain text from ActivityEditInformation TextField inputs
        if (value) {
            parsedValue = `${value}`;
        }
    }

    try {
        if (typeof parsedValue === 'string' && /^\s*</.test(parsedValue)) {
            const { contentBlocks, entityMap } = convertFromHTML(parsedValue);
            return EditorState.createWithContent(
                ContentState.createFromBlockArray(contentBlocks, entityMap),
                decorator
            );
        }
        if (typeof parsedValue === 'string') {
            return EditorState.createWithContent(ContentState.createFromText(parsedValue));
        }
        if (parsedValue && parsedValue !== null) {
            return EditorState.createWithContent(convertFromRaw(parsedValue), decorator);
        }
        // it's null probably
        return EditorState.createWithContent(ContentState.createFromText(''));
    } catch (err) {
        console.log(err);
    }
    return EditorState.createEmpty(decorator);
}

/**
 * True when a Draft.js JSON / HTML / plain-text value has no visible text
 * @param value
 * @returns {boolean}
 */
export function isEditorValueEmpty(value) {
    return (
        !value ||
        !convertJsonOrStringToContext(value)
            .getCurrentContent()
            .hasText()
    );
}

const DraftEditor = ({ value, setValue, label }) => {
    const linkRef = useRef();
    const [urlValue, setUrlValue] = useState('');
    const [showURLInput, setShowURLInput] = useState(false);
    const [editorState, setEditorState] = useState(() => convertJsonOrStringToContext(value));

    const plugins = [LinkPlugin];

    const setFilteredEditorState = useCallback(
        nextState => {
            setEditorState(oldState => {
                const shouldFilterPaste =
                    nextState.getCurrentContent() !== oldState.getCurrentContent() &&
                    nextState.getLastChangeType() === 'insert-fragment';

                if (!shouldFilterPaste) {
                    return nextState;
                }

                return filterEditorState(
                    {
                        blocks: ['unordered-list-item', 'ordered-list-item'],
                        styles: ['BOLD', 'ITALIC', 'UNDERLINE'],
                        entities: [
                            {
                                type: 'LINK',
                                attributes: ['url']
                            }
                        ],
                        maxNesting: 2,
                        whitespacedCharacters: ['\n', '\t', '📷']
                    },
                    nextState
                );
            });
        },
        [setEditorState]
    );

    useEffect(() => {
        // content to save to the db
        const content = editorState.getCurrentContent();
        const contentToSave = JSON.stringify(convertToRaw(content));
        setValue(contentToSave);
    }, [editorState]);

    function handleKeyCommand(command) {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            setFilteredEditorState(newState);
            return true;
        }
        return false;
    }

    function mapKeyToEditorCommand(e) {
        if (e.keyCode === 9 /* TAB */) {
            const newEditorState = RichUtils.onTab(e, editorState, 4 /* maxDepth */);
            if (newEditorState !== editorState) {
                setFilteredEditorState(newEditorState);
            }
            return;
        }
        return getDefaultKeyBinding(e);
    }

    return (
        <>
            <Grid container item xs={12}>
                <Grid container item justifyContent="flex-start" ref={linkRef}>
                    <Grid item style={{ flex: '1 1 0' }}>
                        <p>{label}</p>
                    </Grid>
                    <InlineStyleControls
                        editorState={editorState}
                        onToggle={inlineStyle =>
                            setFilteredEditorState(
                                RichUtils.toggleInlineStyle(editorState, inlineStyle)
                            )
                        }
                    />
                    <BlockStyleControls
                        editorState={editorState}
                        onToggle={blockType =>
                            setFilteredEditorState(
                                RichUtils.toggleBlockType(editorState, blockType)
                            )
                        }
                    />
                    <StyleButton
                        onToggle={() => {
                            promptForLink(editorState, setShowURLInput, setUrlValue);
                        }}
                        label="Add Link"
                        icon="link"
                    />
                    <StyleButton
                        onToggle={() => {
                            removeLink(editorState, setFilteredEditorState);
                        }}
                        label="Remove Link"
                        icon="link-slash"
                    />
                </Grid>
            </Grid>
            {showURLInput && (
                <LinkPopover
                    linkRef={linkRef}
                    urlValue={urlValue}
                    setUrlValue={setUrlValue}
                    editorState={editorState}
                    setFilteredEditorState={setFilteredEditorState}
                    setShowURLInput={setShowURLInput}
                />
            )}
            <Grid item xs={12} className="richEditorRoot">
                <Editor
                    handleKeyCommand={command => handleKeyCommand(command)}
                    keyBindingFn={e => mapKeyToEditorCommand(e)}
                    editorState={editorState}
                    onChange={setFilteredEditorState}
                    placeholder={label}
                    // links are working, but really confusing now
                    plugins={plugins}
                />
            </Grid>
        </>
    );
};

export default withTheme(DraftEditor);
